/**
 * Pre-chat contact form component.
 *
 * Shown upfront (or after the first message, in `delayed` mode) when the widget
 * is configured to capture visitor info before / during chatting. Differs from
 * the mid-chat frustration `ContactForm`:
 *   - adds an optional phone input
 *   - exposes a "Skip" button when the trigger mode allows it (`soft`,
 *     `delayed`); the `hard` mode does not allow skipping and the chat input
 *     is gated separately by the host on the `pre_chat_received` ack
 *
 * Labels arrive from the backend inside the `/api/embed/<id>` response
 * (mirrors the localization pattern used by `ContactForm` for the frustration
 * form). English fallbacks below keep the form renderable if the backend
 * response is missing labels for any reason.
 */

import type { PreChatFormLabels } from "../types/messages";

export interface PreChatFormElements {
  container: HTMLDivElement;
  nameInput: HTMLInputElement;
  emailInput: HTMLInputElement;
  phoneInput: HTMLInputElement;
}

export interface PreChatFormOptions {
  /** Show a "Skip" button next to Submit. False for `hard` mode. */
  allowSkip: boolean;
  /** Initial name pre-fill (from `identify()` if available). */
  initialName?: string;
  /** Initial email pre-fill (from `identify()` if available). */
  initialEmail?: string;
  /** Localized strings shipped from the backend. English fallbacks apply per-field. */
  labels?: PreChatFormLabels;
  onSubmit: (name: string, email: string, phone: string | undefined) => void;
  onSkip?: () => void;
}

export function createPreChatForm(
  options: PreChatFormOptions,
): PreChatFormElements {
  const { allowSkip, initialName, initialEmail, labels, onSubmit, onSkip } =
    options;

  const container = document.createElement("div");
  container.className = "contact-form pre-chat-form";

  const header = document.createElement("p");
  header.className = "contact-form-header";
  header.textContent =
    labels?.header ?? "Let us know who you are before we chat:";
  container.appendChild(header);

  const nameInput = document.createElement("input");
  nameInput.type = "text";
  nameInput.placeholder = labels?.name_placeholder ?? "Your name";
  nameInput.className = "contact-form-input";
  nameInput.required = true;
  if (initialName) nameInput.value = initialName;
  container.appendChild(nameInput);

  const emailInput = document.createElement("input");
  emailInput.type = "email";
  emailInput.placeholder = labels?.email_placeholder ?? "Your email";
  emailInput.className = "contact-form-input";
  emailInput.required = true;
  if (initialEmail) emailInput.value = initialEmail;
  container.appendChild(emailInput);

  const phoneInput = document.createElement("input");
  phoneInput.type = "tel";
  phoneInput.placeholder = labels?.phone_placeholder ?? "Your phone (optional)";
  phoneInput.className = "contact-form-input";
  container.appendChild(phoneInput);

  const errorMessage = document.createElement("p");
  errorMessage.className = "contact-form-error";
  errorMessage.style.display = "none";
  container.appendChild(errorMessage);

  const actions = document.createElement("div");
  actions.className = "pre-chat-form-actions";

  const submitButton = document.createElement("button");
  submitButton.className = "contact-form-button";
  submitButton.textContent = labels?.submit ?? "Send";
  submitButton.type = "button";
  actions.appendChild(submitButton);

  if (allowSkip) {
    const skipButton = document.createElement("button");
    skipButton.className = "pre-chat-form-skip";
    skipButton.textContent = labels?.skip ?? "Skip";
    skipButton.type = "button";
    skipButton.addEventListener("click", () => {
      if (onSkip) onSkip();
    });
    actions.appendChild(skipButton);
  }

  container.appendChild(actions);

  function validate(): boolean {
    const name = nameInput.value.trim();
    const email = emailInput.value.trim();

    if (!name) {
      errorMessage.textContent =
        labels?.name_required ?? "Please enter your name";
      errorMessage.style.display = "block";
      nameInput.focus();
      return false;
    }

    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!email || !emailPattern.test(email)) {
      errorMessage.textContent =
        labels?.email_invalid ?? "Please enter a valid email address";
      errorMessage.style.display = "block";
      emailInput.focus();
      return false;
    }

    errorMessage.style.display = "none";
    return true;
  }

  function handleSubmit(): void {
    if (!validate()) return;
    nameInput.disabled = true;
    emailInput.disabled = true;
    phoneInput.disabled = true;
    submitButton.disabled = true;
    submitButton.textContent = labels?.sending ?? "Sending...";
    const trimmedPhone = phoneInput.value.trim();
    onSubmit(
      nameInput.value.trim(),
      emailInput.value.trim(),
      trimmedPhone || undefined,
    );
  }

  submitButton.addEventListener("click", handleSubmit);

  // Enter on email or phone submits — phone is optional, so email-Enter is
  // a common path for visitors who don't want to type a phone number.
  emailInput.addEventListener("keydown", (event) => {
    if (event.key === "Enter") {
      event.preventDefault();
      handleSubmit();
    }
  });
  phoneInput.addEventListener("keydown", (event) => {
    if (event.key === "Enter") {
      event.preventDefault();
      handleSubmit();
    }
  });

  return { container, nameInput, emailInput, phoneInput };
}
